<template>
    <div>
        <v-row class="mb-4">
            <v-col cols="12" md="2">
                <LocaleSwitcher @change="changeActiveLangCode" />
            </v-col>
            <v-col cols="12" md="10" class="d-flex justify-md-end">
                <v-switch
                    v-model="locationData.active"
                    :label="locationData.active ? t('active') : t('inactive')"
                    color="primary"
                    density="compact"
                    hide-details
                    data-cy="status-switch"
                />
            </v-col>
        </v-row>
        <v-form ref="form" v-model="valid" @keyup.native.enter="save">
            <v-row>
                <v-col cols="12" md="6" class="mb-4 mb-md-0">
                    <v-row align="center">
                        <v-col cols="12">
                            <v-text-field
                                v-model="locationData.name[activeLangCode]"
                                :label="t('name')"
                                :rules="validationRules('name', [requiredRule])"
                                density="compact"
                                variant="outlined"
                                color="primary"
                                data-cy="name-input"
                                hide-details
                            >
                                <template v-slot:append-inner>
                                    <span
                                        v-if="activeLangCode"
                                        class="fi"
                                        :class="[`fi-${useFlagIconCode(activeLangCode)}`]"
                                    />
                                </template>
                            </v-text-field>
                        </v-col>
                        <v-col cols="12">
                            <city-autocomplete
                                v-model="city"
                                :show-icon="false"
                                :rules="validationRules('city', [requiredRule])"
                                :base-label="t('city')"
                                label="city"
                                dense
                                hide-details
                                class="input--no-arrows"
                            />
                        </v-col>
                        <v-col cols="12">
                            <v-text-field
                                v-model="locationData.street"
                                :label="t('address')"
                                density="compact"
                                variant="outlined"
                                color="primary"
                                hide-details
                                data-cy="address-input"
                            />
                        </v-col>
                        <v-col cols="12">
                            <v-text-field
                                v-model="locationData.postalCode"
                                :label="t('postal_code')"
                                density="compact"
                                variant="outlined"
                                color="primary"
                                hide-details
                                data-cy="postalCode-input"
                            />
                        </v-col>
                        <v-col cols="6">
                            <v-text-field
                                v-model="locationData.latitude"
                                :label="t('latitude')"
                                density="compact"
                                variant="outlined"
                                color="primary"
                                type="text"
                                hide-details
                                class="input--no-arrows"
                                data-cy="latitude-input"
                                @update:model-value="getFormattedCoordinates($event, 'latitude')"
                            />
                        </v-col>
                        <v-col cols="6">
                            <v-text-field
                                v-model="locationData.longitude"
                                :label="t('longitude')"
                                density="compact"
                                variant="outlined"
                                color="primary"
                                type="text"
                                hide-details
                                class="input--no-arrows"
                                data-cy="longitude-input"
                                @update:model-value="getFormattedCoordinates($event, 'longitude')"
                            />
                        </v-col>
                        <!--waiting google key-->
                        <!--<v-col cols="2">-->
                        <!--    <v-btn-->
                        <!--        icon="mdi-map-search"-->
                        <!--        color="primary"-->
                        <!--        variant="text"-->
                        <!--        size="small"-->
                        <!--        data-cy="getCoordinates-button"-->
                        <!--        @click="getCoordinates"-->
                        <!--    />-->
                        <!--</v-col>-->
                    </v-row>
                </v-col>
                <v-col cols="12" md="6">
                    <MapLocation
                        :city="city"
                        :latitude="locationData.latitude ? Number(locationData.latitude) : null"
                        :longitude="locationData.longitude ? Number(locationData.longitude) : null"
                        height="320px"
                    />
                </v-col>
            </v-row>
            <div class="d-flex align-center mt-4">
                <v-btn color="primary" :loading="isLoading" :disabled="isLoading" @click="save">
                    {{ $t('save') }}
                </v-btn>
                <v-btn variant="text" @click="emit('go-to-table')">
                    {{ $t('cancel') }}
                </v-btn>
            </div>
        </v-form>
    </div>
</template>

<script setup lang="ts">
    import type {LocationPointType} from '~/utils/types/account/locations/types'
    import CityAutocomplete from '~/components/search/forms/CityAutocomplete.vue'
    import MapLocation from '~src/components/info/mapLocation.src'
    import {clean, clone} from '~/utils/helpers'
    import {requiredRule, validationRules} from '~/composables/rules'

    const props = defineProps<{
        data: LocationPointType
    }>()
    const emit = defineEmits(['load-locations', 'go-to-table'])

    const app = useNuxtApp()
    const {t} = useI18n()
    const form = ref(null)

    onMounted(() => {
        city.value = null
        if (props.data) {
            locationData.value = clone(props.data)
            if (locationData.value.cityId) {
                city.value = {
                    name: locationData.value.cityName,
                    id: locationData.value.cityId,
                    countryName: locationData.value.countryName,
                    countryId: locationData.value.countryId,
                    parentName: locationData.value.countryName,
                }

                delete locationData.value.cityName
                delete locationData.value.cityId
                delete locationData.value.countryName
                delete locationData.value.countryId
            }
        }
    })

    const isLoading = ref<boolean>(false)
    const valid = ref<boolean>(false)
    const activeLangCode = ref<string>('en')
    const locationData = ref<LocationPointType>(props.data)
    const city = ref<{name?: string; id?: number; countryName?: string; countryId?: number; parentName?: string}>(null)

    async function save(): Promise<void> {
        form.value.validate()
        if (!valid.value) return

        try {
            isLoading.value = true
            const id = locationData.value.id
            const rq = clone({
                ...locationData.value,
                cityId: city.value?.id,
                countryId: city.value?.countryId,
            })
            delete rq.id
            clean(rq)

            if (id) await app.$api.locationPoints.put(id, rq)
            else await app.$api.locationPoints.post(rq)

            emit('load-locations')
            emit('go-to-table')
        } catch (e) {
            app.$toast.error(t('data_update_fail'))
        } finally {
            isLoading.value = false
        }
    }

    function getFormattedCoordinates(v: number | string, key: string): void {
        v = String(v).replace(',', '.')
        if (isNaN(parseFloat(v))) {
            locationData.value[key] = null
            return
        }
        const count = 10000000
        locationData.value[key] = Math.floor(parseFloat(v) * count) / count
    }

    async function getCoordinates(): Promise<void> {
        try {
            isLoading.value = true
            const street = locationData.value.street || ''
            const cityName = city.value.name || ''
            const countryName = city.value.countryName || ''
            const postalCode = locationData.value.postalCode || ''
            const rq = {
                street_address: street,
                locality: cityName,
                country: countryName,
                postal_code: postalCode,
            }
            const {results} = await app.$api.geocode.get(rq)

            // TODO [P4]: make possibility to choose result
            if (results?.length) {
                locationData.value.latitude = results[0].geometry.location.lat
                locationData.value.longitude = results[0].geometry.location.lng
            }
        } catch (e) {
            app.$toast.error(t('coordinates_load_fail'))
        } finally {
            isLoading.value = false
        }
    }

    const changeActiveLangCode = (code: string): void => {
        activeLangCode.value = code
    }
</script>
